import React, { useState, useRef, useEffect, useCallback } from 'react';
import Papa from 'papaparse';
import { useAuth } from '../hooks/useAuth';
import { deductCredits, logSend, saveDraft, getDrafts, deleteDraft, getAppSettings, saveCSV, getCSVLists, deleteCSV } from '../utils/firestore';
import { isValidEmail, validateEmails } from '../utils/validate';
import { FullLoader } from '../components/Loader';

const DEFAULT_PLACEHOLDERS = ['{{name}}', '{{email}}', '{{company}}', '{{city}}'];

// Stable rich editor that doesn't lose content on tab switch
function RichEditor({ editorRef, htmlSource, contentTab }) {
  React.useEffect(() => {
    // When switching BACK to rich tab, restore from htmlSource
    if (contentTab === 'rich' && editorRef.current) {
      // Only update if content actually differs to avoid cursor jump
      if (editorRef.current.innerHTML !== htmlSource) {
        editorRef.current.innerHTML = htmlSource;
      }
    }
  }, [contentTab]);

  return (
    <div
      ref={editorRef}
      className="rich-editor"
      contentEditable
      suppressContentEditableWarning
      data-placeholder="Write your email here…"
    />
  );
}

export default function Compose() {
  const { currentUser, userProfile, fetchUserProfile } = useAuth();
  const editorRef = useRef(null);
  const isAdmin = userProfile?.role === 'admin';

  const [sendMode, setSendMode] = useState('single');
  const [bulkTab, setBulkTab] = useState('paste');
  const [contentTab, setContentTab] = useState('rich');

  const [singleEmail, setSingleEmail] = useState('');
  const [singleName, setSingleName] = useState('');
  const [extraVars, setExtraVars] = useState([]);
  const [recipients, setRecipients] = useState([]);
  const [pasteText, setPasteText] = useState('');
  const [csvLists, setCsvLists] = useState([]);
  const [csvName, setCsvName] = useState('');
  const [savingCSV, setSavingCSV] = useState(false);
  const [csvHeaders, setCsvHeaders] = useState([]);
  const [previewRecipientIdx, setPreviewRecipientIdx] = useState(0);



  const [subject, setSubject] = useState('');
  const [htmlSource, setHtmlSource] = useState('');
  const [placeholders, setPlaceholders] = useState(DEFAULT_PLACEHOLDERS);
  const [customPh, setCustomPh] = useState('');

  // App settings from Firestore
  const [appSettings, setAppSettings] = useState(null);
  const [fromName, setFromName] = useState('');
  const [fromPrefix, setFromPrefix] = useState('');
  const [replyTo, setReplyTo] = useState('cmfellowship-mah@gov.in');
  const fromEmail = fromPrefix ? `${fromPrefix}@cmfellowship.in` : '';

  // Drafts
  const [drafts, setDrafts] = useState([]);
  const [currentDraftId, setCurrentDraftId] = useState(null);
  const [showDrafts, setShowDrafts] = useState(false);
  const [draftSaving, setDraftSaving] = useState(false);
  const [draftSaved, setDraftSaved] = useState(false);

  const [alert, setAlert] = useState(null);
  const [sending, setSending] = useState(false);

  const credits = userProfile?.credits ?? 0;
  const isUnlimited = isAdmin && (userProfile?.unlimitedCredits !== false);

  useEffect(() => {
    getAppSettings().then(s => {
      setAppSettings(s);
      setFromName(s.fromNames?.[0] || 'CM Fellowship Maharashtra');
      setFromPrefix(s.fromPrefixes?.[0] || 'CMFellowship-Alert');
      setReplyTo(s.replyTo || 'cmfellowship-mah@gov.in');
    });
    loadDrafts();
    loadCSVLists();
  }, []);

  async function loadCSVLists() {
    const lists = await getCSVLists(currentUser.uid);
    setCsvLists(lists);
  }

  async function handleSaveCSV() {
    if (!recipients.length) { showAlert('No recipients to save', 'danger'); return; }
    const name = csvName.trim() || `List ${new Date().toLocaleDateString()}`;
    setSavingCSV(true);
    await saveCSV(currentUser.uid, { name, contacts: recipients });
    setCsvName('');
    await loadCSVLists();
    showAlert(`"${name}" saved with ${recipients.length} contacts`);
    setSavingCSV(false);
  }

  async function handleDeleteCSV(csvId, name, e) {
    e.stopPropagation();
    if (!window.confirm(`Delete "${name}"?`)) return;
    await deleteCSV(currentUser.uid, csvId);
    await loadCSVLists();
    showAlert('List deleted');
  }

  function loadCSV(list) {
    setRecipients(list.contacts || []);
    showAlert(`"${list.name}" loaded — ${list.contacts?.length} contacts`);
  }

  async function loadDrafts() {
    const d = await getDrafts(currentUser.uid);
    setDrafts(d);
  }

  function showAlert(msg, type = 'success') {
    setAlert({ msg, type });
    setTimeout(() => setAlert(null), 6000);
  }

  function getEditorHTML() {
    return editorRef.current ? editorRef.current.innerHTML : htmlSource;
  }

  function fmt(cmd) { document.execCommand(cmd, false, null); }

  function syncHtmlToRich() {
    if (editorRef.current) editorRef.current.innerHTML = htmlSource;
  }

  function onContentTabChange(tab) {
    // Always save current rich editor content before switching
    if (contentTab === 'rich' && editorRef.current) {
      setHtmlSource(editorRef.current.innerHTML);
    }
    setContentTab(tab);
  }

  function replacePlaceholders(text, vars) {
    return Object.entries(vars).reduce((out, [k, v]) => out.split(`{{${k}}}`).join(v || ''), text);
  }

  function getPreviewHTML() {
    if (sendMode === 'bulk' && recipients.length > 0) {
      const rec = recipients[previewRecipientIdx] || recipients[0];
      return replacePlaceholders(getEditorHTML(), { name: rec.name || '', email: rec.email, ...rec });
    }
    const vars = { name: singleName || '{{name}}', email: singleEmail || '{{email}}' };
    extraVars.forEach(v => { vars[v.key] = v.val; });
    return replacePlaceholders(getEditorHTML(), vars);
  }

  function getPreviewSubject() {
    if (sendMode === 'bulk' && recipients.length > 0) {
      const rec = recipients[previewRecipientIdx] || recipients[0];
      return replacePlaceholders(subject, { name: rec.name || '', email: rec.email, ...rec });
    }
    const vars = { name: singleName || '{{name}}', email: singleEmail || '{{email}}' };
    extraVars.forEach(v => { vars[v.key] = v.val; });
    return replacePlaceholders(subject, vars);
  }


  function parsePastedEmails() {
    const raw = pasteText.split(/[\n,;]+/).map(e => e.trim().toLowerCase()).filter(Boolean);
    const { valid, invalid } = validateEmails(raw);
    const unique = [...new Set(valid)];
    setRecipients(unique.map(e => ({ email: e, name: '' })));
    if (invalid.length) showAlert(`Skipped ${invalid.length} invalid email${invalid.length !== 1 ? 's' : ''}: ${invalid.slice(0,3).join(', ')}`, 'warn');
    else if (unique.length) showAlert(`${unique.length} valid email${unique.length !== 1 ? 's' : ''} added`);
  }

  function parseCSV(file) {
    Papa.parse(file, {
      header: true, skipEmptyLines: true,
      complete: (results) => {
        const rows = results.data;
        const emailKey = Object.keys(rows[0] || {}).find(k => k.toLowerCase().includes('email'));
        const nameKey = Object.keys(rows[0] || {}).find(k => k.toLowerCase() === 'name' || k.toLowerCase() === 'full name');
        if (!emailKey) { showAlert('No "email" column found in CSV', 'danger'); return; }
        // Auto-suggest headers as placeholder chips
        const headers = Object.keys(rows[0] || {});
        setCsvHeaders(headers);
        // Add all headers as placeholders
        const newPhs = headers.map(h => `{{${h}}}`).filter(p => !DEFAULT_PLACEHOLDERS.includes(p));
        setPlaceholders(prev => [...new Set([...prev, ...newPhs])]);
        const recs = rows.map(row => ({
          email: row[emailKey]?.trim().toLowerCase() || '',
          name: nameKey ? row[nameKey]?.trim() : '',
          ...row,
        })).filter(r => isValidEmail(r.email));
        const skipped = rows.length - 1 - recs.length;
        setRecipients(recs);
        showAlert(`${recs.length} valid contacts loaded${skipped > 0 ? `, ${skipped} skipped` : ''}`);
      },
    });
  }

  function insertPlaceholder(ph) {
    if (editorRef.current) {
      editorRef.current.focus();
      document.execCommand('insertText', false, ph);
    }
  }

  function addCustomPlaceholder() {
    let val = customPh.trim();
    if (!val) return;
    if (!val.startsWith('{{')) val = '{{' + val;
    if (!val.endsWith('}}')) val = val + '}}';
    if (!placeholders.includes(val)) setPlaceholders(prev => [...prev, val]);
    insertPlaceholder(val);
    setCustomPh('');
  }

  async function handleSaveDraft() {
    setDraftSaving(true);
    const draft = {
      id: currentDraftId,
      subject,
      htmlBody: getEditorHTML(),
      fromName, fromPrefix, replyTo,
      sendMode, singleEmail, singleName,
      recipients, extraVars,
      savedAt: new Date().toISOString(),
    };
    const id = await saveDraft(currentUser.uid, draft);
    setCurrentDraftId(id);
    setDraftSaving(false);
    setDraftSaved(true);
    setTimeout(() => setDraftSaved(false), 2000);
    loadDrafts();
    loadCSVLists();
  }

  function loadDraft(draft) {
    setCurrentDraftId(draft.id);
    setSubject(draft.subject || '');
    setFromName(draft.fromName || '');
    setFromPrefix(draft.fromPrefix || '');
    setReplyTo(draft.replyTo || '');
    setSendMode(draft.sendMode || 'single');
    setSingleEmail(draft.singleEmail || '');
    setSingleName(draft.singleName || '');
    setRecipients(draft.recipients || []);
    setExtraVars(draft.extraVars || []);
    if (editorRef.current && draft.htmlBody) editorRef.current.innerHTML = draft.htmlBody;
    setShowDrafts(false);
    showAlert('Draft loaded');
  }

  async function handleDeleteDraft(id, e) {
    e.stopPropagation();
    await deleteDraft(currentUser.uid, id);
    if (currentDraftId === id) setCurrentDraftId(null);
    loadDrafts();
    loadCSVLists();
  }

  const recipientCount = sendMode === 'single' ? 1 : recipients.length;
  const canSend = (isUnlimited || credits >= recipientCount) && recipientCount > 0;

  async function handleSend() {
    const html = getEditorHTML();
    if (!subject.trim()) { showAlert('Please enter a subject line', 'danger'); return; }
    if (!fromPrefix.trim()) { showAlert('Please select a from email', 'danger'); return; }
    if (sendMode === 'single' && !singleEmail.trim()) { showAlert('Please enter recipient email', 'danger'); return; }
    if (sendMode === 'single' && !isValidEmail(singleEmail)) { showAlert('Please enter a valid recipient email address', 'danger'); return; }
    if (sendMode === 'bulk' && !recipients.length) { showAlert('Please add recipients', 'danger'); return; }
    if (!canSend) { showAlert('Not enough credits', 'danger'); return; }

    const recs = sendMode === 'single'
      ? [{ email: singleEmail, name: singleName, ...Object.fromEntries(extraVars.map(v => [v.key, v.val])) }]
      : recipients;

    setSending(true);
    try {
      const res = await fetch('/api/send-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fromName, fromEmail, replyTo, subject, htmlBody: html, recipients: recs, mode: sendMode }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Send failed');

      if (!isUnlimited) await deductCredits(currentUser.uid, recs.length);
      // Store rendered HTML for first recipient as preview (placeholders replaced)
      const previewVars = { name: recs[0]?.name || '', email: recs[0]?.email || '', ...recs[0] };
      const renderedPreview = replacePlaceholders(html, previewVars);
      await logSend(currentUser.uid, {
        subject: replacePlaceholders(subject, previewVars),
        recipients: recs.map(r => r.email),
        count: recs.length, status: 'sent', mode: sendMode,
        htmlBody: renderedPreview, fromEmail, fromName,
      });
      await fetchUserProfile(currentUser.uid);
      // Clear draft if this was sent from a draft
      if (currentDraftId) { await deleteDraft(currentUser.uid, currentDraftId); setCurrentDraftId(null); loadDrafts(); }
      showAlert(`${recs.length} email${recs.length !== 1 ? 's' : ''} sent successfully!`);
      // Clear compose form after successful send
      setSubject('');
      setSingleEmail('');
      setSingleName('');
      setRecipients([]);
      setExtraVars([]);
      setPasteText('');
      setCsvHeaders([]);
      setPlaceholders(DEFAULT_PLACEHOLDERS);
      if (editorRef.current) editorRef.current.innerHTML = '';
    } catch (err) {
      showAlert(err.message, 'danger');
    }
    setSending(false);
  }

  return (
    <>
      <div className="topbar">
        <div>
          <div className="topbar-title">Compose & send {currentDraftId && <span style={{ fontSize: 12, color: 'var(--text3)', fontWeight: 400 }}>— editing draft</span>}</div>
          <div className="topbar-sub">Draft your email and send to one or many recipients</div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {drafts.length > 0 && (
            <button className="btn btn-sm" onClick={() => setShowDrafts(!showDrafts)}>
              📝 Drafts ({drafts.length})
            </button>
          )}
          <span className={`badge ${isUnlimited ? 'badge-info' : credits > 20 ? 'badge-success' : 'badge-danger'}`}>
            {isUnlimited ? '∞ Unlimited' : `${credits} credits`}
          </span>
        </div>
      </div>

      {/* Drafts panel */}
      {showDrafts && (
        <div style={{ background: 'var(--bg2)', borderBottom: '0.5px solid var(--border)', padding: '12px 24px' }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text2)', marginBottom: 10 }}>Saved drafts</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {drafts.map(d => (
              <div key={d.id} onClick={() => loadDraft(d)}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', background: 'var(--bg)', border: '0.5px solid var(--border)', borderRadius: 'var(--radius)', cursor: 'pointer' }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 500 }}>{d.subject || '(no subject)'}</div>
                  <div style={{ fontSize: 11, color: 'var(--text3)' }}>{d.updatedAt?.toDate ? d.updatedAt.toDate().toLocaleString() : d.savedAt || ''}</div>
                </div>
                <button className="btn btn-sm btn-danger" onClick={(e) => handleDeleteDraft(d.id, e)}>Delete</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {sending && <FullLoader message={sendMode === 'bulk' ? `Sending to ${recipients.length} recipients…` : 'Sending email…'} />}
      <div className="content">
        {alert && <div className={`alert alert-${alert.type}`}>{alert.msg}</div>}

        {/* Send mode */}
        <div className="card">
          <div className="card-title">Send mode</div>
          <div className="mode-tabs">
            <div className={`mode-tab ${sendMode === 'single' ? 'active' : ''}`} onClick={() => setSendMode('single')}>
              <div style={{ fontSize: 20, marginBottom: 4 }}>✉</div>Single email
            </div>
            <div className={`mode-tab ${sendMode === 'bulk' ? 'active' : ''}`} onClick={() => setSendMode('bulk')}>
              <div style={{ fontSize: 20, marginBottom: 4 }}>📨</div>Bulk send
            </div>
          </div>

          {sendMode === 'single' && (
            <>
              <div className="row">
                <div className="col field">
                  <label className="field-label">Recipient email</label>
                  <input type="email" value={singleEmail} onChange={e => setSingleEmail(e.target.value)} placeholder="fellow@cmfellowship.in" />
                </div>
                <div className="col field">
                  <label className="field-label">Recipient name (for {`{{name}}`})</label>
                  <input type="text" value={singleName} onChange={e => setSingleName(e.target.value)} placeholder="Priya Sharma" />
                </div>
              </div>
              <div className="divider" />
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text2)', marginBottom: 10 }}>Extra placeholder values</div>
              {extraVars.map((v, i) => (
                <div key={i} className="row" style={{ marginBottom: 8, alignItems: 'center' }}>
                  <div className="col"><input type="text" placeholder="variable name" value={v.key} onChange={e => { const n = [...extraVars]; n[i].key = e.target.value; setExtraVars(n); }} /></div>
                  <div className="col"><input type="text" placeholder="value" value={v.val} onChange={e => { const n = [...extraVars]; n[i].val = e.target.value; setExtraVars(n); }} /></div>
                  <button className="btn btn-sm btn-danger" onClick={() => setExtraVars(extraVars.filter((_, j) => j !== i))}>✕</button>
                </div>
              ))}
              <button className="btn btn-sm mt-8" onClick={() => setExtraVars([...extraVars, { key: '', val: '' }])}>+ Add variable</button>
            </>
          )}

          {sendMode === 'bulk' && (
            <>
              <div className="tabs">
                {['paste', 'csv'].map(t => (
                  <div key={t} className={`tab ${bulkTab === t ? 'active' : ''}`} onClick={() => setBulkTab(t)}>
                    {t === 'paste' ? 'Paste emails' : 'Upload CSV'}
                  </div>
                ))}
              </div>
              {bulkTab === 'paste' && (
                <>
                  <label className="field-label">Paste emails (one per line or comma-separated)</label>
                  <textarea rows={4} value={pasteText} onChange={e => setPasteText(e.target.value)} placeholder={"alice@cmfellowship.in\nbob@cmfellowship.in"} />
                  <button className="btn btn-sm mt-8" onClick={parsePastedEmails}>Parse emails</button>
                </>
              )}
              {bulkTab === 'csv' && (
                <>
                  <div className="csv-drop" onClick={() => document.getElementById('csv-input').click()}>
                    <div style={{ fontSize: 28, marginBottom: 8 }}>📄</div>
                    Click to upload CSV<br />
                    <span style={{ fontSize: 11 }}>Columns: email, name, + any custom placeholder fields</span>
                  </div>
                  <input id="csv-input" type="file" accept=".csv" style={{ display: 'none' }} onChange={e => e.target.files[0] && parseCSV(e.target.files[0])} />

                  {/* Save current recipients as a named list */}
                  {recipients.length > 0 && (
                    <div style={{ display: 'flex', gap: 8, marginTop: 12, alignItems: 'center' }}>
                      <input type="text" value={csvName} onChange={e => setCsvName(e.target.value)}
                        placeholder={`Name this list (${recipients.length} contacts)`} style={{ flex: 1, fontSize: 12 }} />
                      <button className="btn btn-sm btn-primary" onClick={handleSaveCSV} disabled={savingCSV}>
                        {savingCSV ? 'Saving…' : '💾 Save list'}
                      </button>
                    </div>
                  )}

                  {/* Saved CSV lists */}
                  {csvLists.length > 0 && (
                    <div style={{ marginTop: 16 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text2)', marginBottom: 8 }}>Saved lists</div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {csvLists.map(list => (
                          <div key={list.id} onClick={() => loadCSV(list)} style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            padding: '9px 12px', border: '1px solid var(--border)', borderRadius: 'var(--radius)',
                            cursor: 'pointer', background: 'var(--bg)', transition: 'all 0.12s',
                          }}>
                            <div>
                              <div style={{ fontSize: 13, fontWeight: 600 }}>📋 {list.name}</div>
                              <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>
                                {list.count} contacts · saved {list.savedAt?.toDate ? list.savedAt.toDate().toLocaleDateString() : ''}
                              </div>
                            </div>
                            <button className="btn btn-sm btn-danger" onClick={e => handleDeleteCSV(list.id, list.name, e)}>✕ Delete</button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
              {recipients.length > 0 && (
                <div style={{ marginTop: 16 }}>
                  <label className="field-label">
                    Recipients ({recipients.length}) —{' '}
                    <span style={{ color: (isUnlimited || credits >= recipients.length) ? 'var(--accent)' : 'var(--danger)' }}>
                      {isUnlimited ? 'unlimited sending' : `${recipients.length} credits needed${credits < recipients.length ? ' — not enough!' : ''}`}
                    </span>
                  </label>
                  <div className="tags-area">
                    {recipients.map((r, i) => (
                      <div key={i} className="tag">
                        {r.name && <span style={{ fontWeight: 500 }}>{r.name} </span>}
                        <span style={{ color: 'var(--text3)' }}>{r.email}</span>
                        <button className="tag-remove" onClick={() => setRecipients(recipients.filter((_, j) => j !== i))}>✕</button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Email content */}
        <div className="card">
          <div className="card-title">Email content</div>
          <div className="field">
            <label className="field-label">Subject line</label>
            <input type="text" value={subject} onChange={e => setSubject(e.target.value)} placeholder="Hello {{name}}, here's your update from CM Fellowship" />
          </div>
          <div className="tabs">
            {[['rich', 'Rich editor'], ['html', 'HTML source'], ['preview', 'Preview']].map(([k, v]) => (
              <div key={k} className={`tab ${contentTab === k ? 'active' : ''}`} onClick={() => onContentTabChange(k)}>{v}</div>
            ))}
          </div>
          {contentTab === 'rich' && (
            <>
              <div className="editor-toolbar">
                <button className="tb-btn" onClick={() => fmt('bold')}><b>B</b></button>
                <button className="tb-btn" onClick={() => fmt('italic')}><i>I</i></button>
                <button className="tb-btn" onClick={() => fmt('underline')}><u>U</u></button>
                <button className="tb-btn" onClick={() => fmt('insertUnorderedList')}>• List</button>
                <button className="tb-btn" onClick={() => fmt('insertOrderedList')}>1. List</button>
                <button className="tb-btn" onClick={() => { const url = prompt('URL:', 'https://'); if (url) document.execCommand('createLink', false, url); }}>Link</button>
                <button className="tb-btn" onClick={() => fmt('justifyLeft')}>≡L</button>
                <button className="tb-btn" onClick={() => fmt('justifyCenter')}>≡C</button>
                <button className="tb-btn" onClick={() => fmt('justifyRight')}>≡R</button>
              </div>
              <RichEditor editorRef={editorRef} htmlSource={htmlSource} contentTab={contentTab} />
            </>
          )}
          {contentTab === 'html' && (
            <>
              <div className="editor-toolbar" style={{ borderRadius: 'var(--radius) var(--radius) 0 0' }}>
                <span style={{ fontSize: 11, color: 'var(--text3)' }}>HTML source — changes sync to rich editor on switch</span>
              </div>
              <textarea className="html-editor" rows={10} value={htmlSource} onChange={e => setHtmlSource(e.target.value)} onBlur={syncHtmlToRich} />
            </>
          )}
          {contentTab === 'preview' && (
            <>
              <div className="editor-toolbar" style={{ borderRadius: 'var(--radius) var(--radius) 0 0', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                <span style={{ fontSize: 11, color: 'var(--text3)' }}>Preview — placeholders replaced with real values</span>
                {sendMode === 'bulk' && recipients.length > 0 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 11, color: 'var(--text3)' }}>Preview as:</span>
                    <select value={previewRecipientIdx} onChange={e => setPreviewRecipientIdx(Number(e.target.value))}
                      style={{ fontSize: 11, padding: '2px 6px', width: 'auto' }}>
                      {recipients.map((r, i) => (
                        <option key={i} value={i}>{r.name || r.email} ({r.email})</option>
                      ))}
                    </select>
                    <span style={{ fontSize: 11, color: 'var(--text3)' }}>{previewRecipientIdx + 1}/{recipients.length}</span>
                  </div>
                )}
              </div>
              {subject && (
                <div style={{ padding: '10px 14px', background: 'var(--bg2)', borderLeft: '1.5px solid var(--border2)', borderRight: '1.5px solid var(--border2)', fontSize: 13 }}>
                  <span style={{ color: 'var(--text3)', fontSize: 11, fontWeight: 700 }}>SUBJECT: </span>
                  {getPreviewSubject()}
                </div>
              )}
              <div className="preview-frame" dangerouslySetInnerHTML={{ __html: getPreviewHTML() }} />
            </>
          )}
          <div style={{ marginTop: 14 }}>
            {csvHeaders.length > 0 && (
              <div style={{ fontSize: 11, color: 'var(--accent-text)', background: 'var(--accent-bg)', padding: '6px 10px', borderRadius: 'var(--radius)', marginBottom: 8, fontWeight: 500 }}>
                ✓ CSV headers auto-added as placeholders: {csvHeaders.map(h => `{{${h}}}`).join(', ')}
              </div>
            )}
            <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 8 }}>Insert placeholder:</div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {placeholders.map(p => <span key={p} className="chip" onClick={() => insertPlaceholder(p)}>{p}</span>)}
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
              <input type="text" value={customPh} onChange={e => setCustomPh(e.target.value)} placeholder="Add custom: {{product}}" style={{ fontSize: 12 }} onKeyDown={e => e.key === 'Enter' && addCustomPlaceholder()} />
              <button className="btn btn-sm" onClick={addCustomPlaceholder}>Add</button>
            </div>
          </div>
        </div>

        {/* Send config */}
        <div className="card">
          <div className="card-title">Send configuration</div>
          <div className="row">
            <div className="col field">
              <label className="field-label">From name</label>
              {isAdmin ? (
                <input type="text" value={fromName} onChange={e => setFromName(e.target.value)} />
              ) : (
                <select value={fromName} onChange={e => setFromName(e.target.value)}>
                  {(appSettings?.fromNames || []).map(n => <option key={n} value={n}>{n}</option>)}
                </select>
              )}
            </div>
            <div className="col field">
              <label className="field-label">From email</label>
              {isAdmin ? (
                <div style={{ display: 'flex', alignItems: 'center', border: '0.5px solid var(--border2)', borderRadius: 'var(--radius)', background: 'var(--bg2)', overflow: 'hidden' }}>
                  <input type="text" value={fromPrefix} onChange={e => setFromPrefix(e.target.value.replace(/@.*/, ''))}
                    style={{ border: 'none', background: 'transparent', flex: 1, padding: '8px 10px', minWidth: 0 }} />
                  <span style={{ padding: '8px 10px 8px 0', color: 'var(--text3)', fontSize: 13, whiteSpace: 'nowrap' }}>@cmfellowship.in</span>
                </div>
              ) : (
                <select value={fromPrefix} onChange={e => setFromPrefix(e.target.value)}>
                  {(appSettings?.fromPrefixes || []).map(p => <option key={p} value={p}>{p}@cmfellowship.in</option>)}
                </select>
              )}
            </div>
          </div>
          <div className="field">
            <label className="field-label">Reply-to {!isAdmin && <span style={{ fontSize: 11, color: 'var(--text3)' }}>(managed by admin)</span>}</label>
            <input type="email" value={replyTo} readOnly={!isAdmin} onChange={e => isAdmin && setReplyTo(e.target.value)}
              style={{ opacity: isAdmin ? 1 : 0.6, cursor: isAdmin ? 'text' : 'default' }} />
          </div>
          <div className="actions">
            <span style={{ fontSize: 12, color: canSend ? 'var(--text3)' : 'var(--danger)' }}>
              {recipientCount > 0 ? (isUnlimited ? 'Unlimited sending' : `${recipientCount} credit${recipientCount !== 1 ? 's' : ''} will be used`) : 'No recipients'}
            </span>
            <button className="btn" onClick={handleSaveDraft} disabled={draftSaving}>
              {draftSaved ? '✓ Saved' : draftSaving ? 'Saving…' : '💾 Save draft'}
            </button>
            <button className="btn btn-primary" onClick={handleSend} disabled={sending || !canSend}>
              {sending ? 'Sending…' : `Send email${recipientCount !== 1 ? 's' : ''}`}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
